import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const formattedOwnerNames = (externalAccount, clientDetails) => {
    const isDeceasedBeneRegType = externalAccount.registrationType === 35 || externalAccount.registrationType === 36;
    const hasSecondaryOwner = !!externalAccount.secondaryOwner;
    const primaryOwner = clientDetails.find(clientDetail => clientDetail.clientId === externalAccount.primaryOwner);
    const secondaryOwner = clientDetails.find(clientDetail => clientDetail.clientId === externalAccount.secondaryOwner);
    const primaryOwnerFormatted = `${primaryOwner.firstName} ${primaryOwner.lastName}`;
    const secondaryOwnerFormatted = hasSecondaryOwner ? `${secondaryOwner.firstName} ${secondaryOwner.lastName}` : '';
    const decedentFormatted = isDeceasedBeneRegType
    ? `${externalAccount.decedentFirstName} 
    ${externalAccount.decedentMiddleName ? externalAccount.decedentMiddleName.concat(' ') : ''}
    ${externalAccount.decedentLastName} 
    ${externalAccount.decedentSuffixName ? externalAccount.decedentSuffixName.concat(' ') : ''}(DCD)`
    : '';
    return `${primaryOwnerFormatted}${hasSecondaryOwner ? `, ${secondaryOwnerFormatted}` : ''}${isDeceasedBeneRegType ? `, ${decedentFormatted}` : ''}`;
};

const regLineMaxWidth = window && window.innerWidth < 960 ? Math.round(window.innerWidth * (1 / 2)) : 350;

const useStyles = makeStyles((theme) => ({
    tableHeaders: {
        color: 'white',
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '14px'
    },
    totalsRow: {
        backgroundColor: '#F4F5F6'
    },
    tRoweHeader: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '14px',
        lineHeight: '14px',
        paddingBottom: '8px'
    },
    agreementTypeName: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '20px',
        lineHeight: '23px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: `${regLineMaxWidth}px`,
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        }
    },
    owners: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '14px'
    },
    agrTotal: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '16px'
    },
    iconBtn: {
        color: '#086EA1',
        paddingRight: 0,
        paddingBottom: 0
    },
    fundName: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '16px'
    },
    ticker: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '12px',
        paddingLeft: '8px'
    },
    agrNum: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '14px'
    },
    agrFundTotal: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '16px'
    },
    agrPercent: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '14px'
    },
    cumlRow: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '16px',
        lineHeight: '20px'
    },
}))

function Row({ externalAccount, clientDetails, index }) {
    const classes = useStyles();

    return (
        <TableRow className={classes.totalsRow} id={`asset-table-row-${index}`}>
            <TableCell id={`agr-info-container-${externalAccount.registrationType}-${index}`}>
            <Typography className={classes.tRoweHeader} id={`t-ext-bank-${externalAccount.registrationType}-${index}`}>
                    <span className={classes.tRoweheader} style={{ whitespace: 'nowrap' }}>{externalAccount.financialInstitutionName.toUpperCase()}</span>
                </Typography>
                <Typography className={classes.agreementTypeName} id={`t-rowe-agrmt-type-${externalAccount.registrationType}-${index}`}>
                    <span style={{ whitespace: 'nowrap'}}>{externalAccount.registrationTypeDisplayName.toUpperCase()}</span>
                </Typography>
                
                <Typography className={`${classes.owners} blocked_data`} id={`agr-owners-${externalAccount.registrationType}-${index}`}>
                    {formattedOwnerNames(externalAccount, clientDetails)}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography className={classes.agrTotal} id={`agr-total-${externalAccount.registrationType}-${index}`}>
                    {formatter.format(externalAccount.amount)}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

/* eslint-disable react/no-array-index-key */
const ExternalTable = ({ externalAccounts, clientDetails, dedicatedTotal }) => {
    const classes = useStyles();

    return (
        <TableContainer component={Paper}>
            <Table aria-label="collapsible table" id='full-assets-table'>
                <TableHead id='assets-table-head'>
                    <TableRow style={{ backgroundColor: '#1E2D39' }} id='assets-table-header-container'>
                        <TableCell align="left" id='curr-acct-cell'>
                            <Typography className={classes.tableHeaders} id='curr-acct-header'>
                                EXTERNAL ACCOUNTS
                            </Typography>                
                        </TableCell>
                        <TableCell align="right" id='tot-avb-cell' />
                    </TableRow>
                </TableHead>
                <TableBody id='asset-table-body'>
                    {externalAccounts.map((externalAccount, index) => (
                        <Row externalAccount={externalAccount} clientDetails={clientDetails} index={index} key={`${externalAccount.financialInstitutionName}-${index}`} />
                    ))}
                    <TableRow style={{ borderTop: '2px solid #05C3DE' }} id='cuml-row'>
                        <TableCell align="left" style={{ padding: '24px 16px' }} id='tot-adv-assets'>
                            <Typography className={classes.cumlRow} id='tot-assets-header'>
                                TOTAL ASSETS FOR ADVISORY SERVICE
                            </Typography>
                        </TableCell>
                        <TableCell align="right" style={{ padding: '24px 16px' }}>
                            <Typography className={classes.cumlRow} id='tot-assets-amt'>
                                {formatter.format(dedicatedTotal)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
};

export default ExternalTable;
